import React, { useRef } from 'react';
import { Box, Flex, Avatar } from '@chakra-ui/react';
import { KText } from '@/ui-library/components/text';
import { convertSecondsToMinSec } from '@/utils/interview-day-format';
import { TIMELINE_COLORS } from '@/components/shared/interviews-revamp/utils/contant';

interface TimelineInterval {
  start: number;
  end: number;
}

interface SpeakerTimeline {
  speaker: string;
  timeline: TimelineInterval[];
  talk_time: number;
}

interface ParticipantsData {
  speaker_timelines?: SpeakerTimeline[];
  transcript_duration: number;
  total_talk_time: number;
}

interface ParticipantsProps {
  data: ParticipantsData;
  handleTimeJump: (time: number) => void;
}

export const Participants = ({ data, handleTimeJump }: ParticipantsProps) => {
  const {
    speaker_timelines: timelines,
    transcript_duration: duration,
    total_talk_time: totalTalkTime,
  } = data || {};
  const containerRef = useRef<HTMLDivElement>(null);

  const calculateWidth = (start: number, end: number): string => {
    const intervalDuration = end - start;
    return `${(intervalDuration / duration) * 100}%`;
  };

  const renderInterval = (
    timelines: TimelineInterval[],
    speakerIndex: number
  ) => {
    let blankStartTime = 0;
    return (
      <>
        {timelines?.map((timeline, index) => {
          const blankWidth = calculateWidth(blankStartTime, timeline.start);
          const blankLeft = `${(blankStartTime / duration) * 100}%`;
          const spokenWidth = calculateWidth(timeline.start, timeline.end);
          const spokenLeft = `${(timeline.start / duration) * 100}%`;
          blankStartTime = timeline.end;
          return (
            <React.Fragment key={index}>
              <Box
                style={{
                  width: blankWidth,
                  left: blankLeft,
                  backgroundColor: 'transparent',
                  position: 'absolute',
                  height: '100%',
                  top: 0,
                }}
              />
              <Box
                style={{
                  width: spokenWidth,
                  left: spokenLeft,
                  backgroundColor: TIMELINE_COLORS[speakerIndex % 4],
                  position: 'absolute',
                  height: '100%',
                  top: 0,
                }}
              />
            </React.Fragment>
          );
        })}
      </>
    );
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const container = containerRef.current;
    if (container) {
      const rect = container.getBoundingClientRect();
      const xPosition = event.clientX - rect.left;
      const width = rect.width;
      const calculatedPercentage = (xPosition / width) * 100;

      handleTimeJump(duration * (calculatedPercentage / 100) * 1000);
    }
  };

  const renderTimeline = (timeline: TimelineInterval[], index: number) => (
    <Flex
      ref={containerRef}
      bg="#F4F1F1"
      h="6px"
      borderRadius="5px"
      position="relative"
      overflow="hidden"
      cursor="pointer"
      onClick={handleClick}
    >
      {renderInterval(timeline, index)}
    </Flex>
  );

  return (
    <Flex flexDirection="column" mt="20px" gap="20px" width="100%">
      <Flex justifyContent="space-between">
        <KText
          display="flex"
          flexDir="row"
          alignItems="center"
          size="sm-semi-bold"
          color="secondary-900"
        >
          Total talk time:{' '}
          <KText size="sm-regular" ml="5px">
            {Math.round((totalTalkTime / duration) * 100)}% (
            {convertSecondsToMinSec(totalTalkTime)})
          </KText>
        </KText>
      </Flex>

      <Flex flexDirection="column" gap="44px" pb="20px">
        {timelines?.map(({ speaker, timeline, talk_time: talkTime }, index) => (
          <Flex key={speaker} flexDirection="column" flex={1}>
            <Flex alignItems="center" justifyContent="space-between" mb="24px">
              <Flex alignItems="center">
                <Avatar name={speaker} size="xs" mr="10px" borderRadius="md" />
                <KText size="sm-semi-bold" color="secondary-900">
                  {speaker}
                </KText>
              </Flex>
              <KText size="sm-medium" color={TIMELINE_COLORS[index % 4]}>
                Total talk time: {Math.round((talkTime / totalTalkTime) * 100)}%
                ({convertSecondsToMinSec(talkTime)})
              </KText>
            </Flex>
            {renderTimeline(timeline, index)}
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
};
